import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Badge } from '@codes/ui-kit/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { useQueryClient } from '@tanstack/react-query';
import { EllipsisVertical, MessageSquare } from 'lucide-react';

import { useDeletePrompt } from '@codes/agents';
import { formatDate } from '@codes/utils';

import { updateCacheWithDeleted } from '../utils';

interface PromptListItemProps {
    prompt: PromptView;
    isSelected: boolean;
    onSelect: () => void;
    onPromptDeleted?: (deletedPromptId: string) => void;
}

export const PromptListItem = ({ prompt, isSelected, onSelect, onPromptDeleted }: PromptListItemProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const deletePromptMutation = useDeletePrompt();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const getStereoColor = (stereo: string | undefined) => {
        return stereo === 'system'
            ? 'bg-[#F0F8FF] text-[#3088CF] border[#3088CF]'
            : 'bg-[#FFF8E8] text-[#F0A066] border-[#F0A066]';
    };

    const getStereoLabel = (stereo: string | undefined) => {
        return stereo === 'system' ? t('prompt.stereo.system') : t('prompt.stereo.user');
    };

    const promptName = prompt.name || t('prompt.listItem.states.noName');
    const promptContent = prompt.contents ? prompt.contents.join(' ').substring(0, 80) : '';
    const hasMoreContent = Boolean(prompt.contents && prompt.contents.join(' ').length > 80);

    const handleDelete = async () => {
        if (!prompt.id) {
            return;
        }

        try {
            await deletePromptMutation.mutateAsync(prompt.id);
            updateCacheWithDeleted(queryClient, 'prompts', prompt.id);
            toast({
                title: t('prompt.listItem.messages.deleteSuccess.title'),
                description: t('prompt.listItem.messages.deleteSuccess.description', { promptName }),
            });
            setIsDeleteDialogOpen(false);

            // If this prompt is currently selected, notify parent to navigate back
            if (isSelected && onPromptDeleted) {
                onPromptDeleted(prompt.id);
            }
        } catch (error) {
            console.error('Error deleting prompt:', error);
            toast({
                title: t('prompt.listItem.messages.deleteError.title'),
                description: t('prompt.listItem.messages.deleteError.description'),
                variant: 'destructive',
            });
        }
    };

    return (
        <>
            <li
                className={`group relative bg-[#f4f5f5] dark:bg-[#222325] border rounded-xl cursor-pointer transition-all duration-200 hover:border-text ${
                    isSelected ? 'border-point' : 'border-chatbot-line'
                }`}
                onClick={onSelect}
            >
                <div className="py-[9px] px-[13px]">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{promptName}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge
                                variant="outline"
                                className={`h-5 text-[13px] font-medium ${getStereoColor(prompt.stereo)}`}
                            >
                                {getStereoLabel(prompt.stereo)}
                            </Badge>
                            {(prompt as any).isStock && (
                                <Badge
                                    variant="secondary"
                                    className="text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                                >
                                    {t('prompt.listItem.states.inStock')}
                                </Badge>
                            )}
                            <DropdownMenu>
                                <DropdownMenuTrigger className="group" onClick={e => e.stopPropagation()}>
                                    <EllipsisVertical className="w-4 h-4 text-sub group-hover:text-icon-hover" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[100px] rounded-lg px-3 py-[2px]">
                                    <DropdownMenuItem
                                        onClick={e => {
                                            e.stopPropagation();
                                            setIsDeleteDialogOpen(true);
                                        }}
                                        className="text-xs text-[#F34822] focus:text-[#F34822]"
                                    >
                                        {t('prompt.listItem.actions.deleteMenuItem')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>

                    {promptContent && (
                        <div className="mb-3">
                            <p
                                className={`text-xs font-medium text-sub leading-relaxed line-clamp-2 ${
                                    isSelected ? '' : ''
                                }`}
                            >
                                {promptContent}
                                {hasMoreContent && '...'}
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-sub font-medium">
                        <div className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            <span>
                                {prompt.contents ? prompt.contents.length : 0} {t('prompt.listItem.labels.lines')}
                            </span>
                        </div>
                        <div className="text-xs">{prompt.createdAt ? formatDate(prompt.createdAt) : ''}</div>
                    </div>
                </div>
            </li>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogTitle>{t('prompt.listItem.dialog.title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('prompt.listItem.dialog.warning')}</AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel type="button" onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('common.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="text-error"
                            onClick={handleDelete}
                            disabled={deletePromptMutation.isPending}
                        >
                            {deletePromptMutation.isPending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    {t('common.loading')}
                                </>
                            ) : (
                                t('prompt.listItem.dialog.actions.delete')
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
